class ScratchCardPopup extends HTMLElement {
  constructor() {
    super();

    this.storageKey = "kfk_scratch_v1";
    this.cookieKey = "__kfk_scratch_popup";
    this.teaserKey = "kfk_scratch_teaser";

    const configEl = this.querySelector("[data-scratch-config]");
    this.config = configEl ? JSON.parse(configEl.textContent) : {};
    this.prizes = this.config.prizes || [];
    // the scratch prizes; the welcome variant swaps in its single fixed code
    this.scratchPrizes = this.prizes;
    this.variant = null;

    this.dialog = this.querySelector('[role="dialog"]');
    this.canvas = this.querySelector("[data-scratch-canvas]");
    this.tapButton = this.querySelector("[data-scratch-tap]");
    this.form = this.querySelector("form");
    this.tagsInput = this.querySelector('input[name="contact[tags]"]');
    this.codeInput = this.querySelector("[data-code-input]");
    this.copyButton = this.querySelector("[data-copy-code]");
    this.applyLink = this.querySelector("[data-apply-link]");

    this.designMode = window.Shopify && window.Shopify.designMode;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // sibling of this element: the popup root is display:none while closed
    this.teaser = document.querySelector("[data-scratch-teaser]");

    this.handleClose();
    this.handleCopy();
    this.handleApply();
    this.handleSubmit();
    this.handleTapReveal();
    this.handleBirthday();
    this.handleTeaser();
    this.watchShopping();

    this.addEventListener("keyup", (e) => {
      if (e.code && e.code.toUpperCase() === "ESCAPE") this.close();
    });

    if (this.designMode) {
      this.setupDesignMode();
      if (this.hasAttribute("open")) this.openDemo();
      return;
    }

    this.route();
  }

  /* ---------- state ---------- */

  setRepeatCookie() {
    const days = {
      no_repeat: 365,
      every_day: 1,
      every_3_days: 3,
      every_week: 7,
      every_month: 30,
    };
    setCookie(this.cookieKey, JSON.stringify({ opened: true }), days[this.config.repeat] || 3);
  }

  readState() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || {};
    } catch (e) {
      return {};
    }
  }

  writeState(state) {
    try {
      // the variant rides along so a split test visitor keeps the one they were given
      const merged = Object.assign({}, state);
      if (this.variant) merged.variant = this.variant;
      localStorage.setItem(this.storageKey, JSON.stringify(merged));
    } catch (e) {
      /* storage unavailable, popup degrades gracefully */
    }
  }

  /* ---------- variant: scratch card or email for a code ---------- */

  pickVariant() {
    const mode = this.config.mode || "scratch";
    if (mode === "welcome") return "welcome";
    if (mode !== "split") return "scratch";
    const state = this.readState();
    if (state.variant === "welcome" || state.variant === "scratch") return state.variant;
    return Math.random() < 0.5 ? "welcome" : "scratch";
  }

  applyVariant(variant) {
    this.variant = variant;
    this.dataset.variant = variant;
    const welcome = this.config.welcome || {};
    this.prizes =
      variant === "welcome"
        ? [{ label: welcome.label || "", code: welcome.code || "", weight: 1 }]
        : this.scratchPrizes;
    this.querySelectorAll("[data-copy-scratch]").forEach((el) => el.toggleAttribute("hidden", variant === "welcome"));
    this.querySelectorAll("[data-copy-welcome]").forEach((el) => el.toggleAttribute("hidden", variant !== "welcome"));
  }

  firstStage() {
    return this.variant === "welcome" ? "claim" : "scratch";
  }

  // the welcome variant has one prize, so a stored scratch index never applies to it
  prizeFromState(state) {
    return this.variant === "welcome" ? 0 : state.prize;
  }

  openDelay() {
    const welcome = this.config.welcome || {};
    const raw = this.variant === "welcome" ? welcome.delay : this.config.delay;
    const n = parseInt(raw, 10);
    return isNaN(n) ? 4 : n;
  }

  route() {
    this.applyVariant(this.pickVariant());
    const state = this.readState();

    if (state.state === "claimed") return;

    const hasError = !!this.querySelector("[data-form-error]");

    if (state.state === "pending_claim") {
      if (hasError) {
        // submission bounced: back to the claim step with the error visible
        this.writeState({ state: "revealed", prize: state.prize });
        this.setPrize(this.prizeFromState(state));
        this.setStage("claim");
      } else {
        // they submitted; wherever Shopify lands them (incl. after its captcha),
        // the birthday step (or the code) must be waiting
        this.writeState({ state: "claimed", prize: state.prize });
        this.setPrize(this.prizeFromState(state));
        this.applyCode();
        this.setStage(this.nextAfterClaim());
      }
      this.open(true);
      return;
    }

    if (state.state === "revealed") {
      // Scratched but never claimed: bring them straight back to the claim step
      const cookieData = getCookie(this.cookieKey);
      if (cookieData) {
        // the auto-open is suppressed, so the teaser tab is the way back in
        this.showTeaser();
        return;
      }
      this.setPrize(this.prizeFromState(state));
      this.setStage("claim");
      this.scheduleOpen();
      return;
    }

    const cookieData = getCookie(this.cookieKey);
    if (cookieData) {
      this.showTeaser();
      return;
    }

    if (this.variant === "welcome") {
      this.setPrize(0);
    } else {
      this.setPrize(typeof state.prize === "number" ? state.prize : this.pickPrize());
    }
    this.setStage(this.firstStage());
    this.scheduleOpen();
  }

  scheduleOpen() {
    setTimeout(() => this.whenConsentSettled(() => this.whenNotShopping(() => this.open())), this.openDelay() * 1000);
  }

  // Never open over the bag. If she has just added to cart, or a drawer (cart, menu, filters) is
  // open, wait until it has been quiet for a few seconds, then open. Daniil, 2026-10-17: the
  // popup landed on top of the cart drawer the instant an Instagram shopper tapped Add to cart.
  watchShopping() {
    this.lastCartAt = 0;
    const mark = () => {
      this.lastCartAt = Date.now();
    };
    document.addEventListener(
      "submit",
      (e) => {
        const action = (e.target && e.target.getAttribute && e.target.getAttribute("action")) || "";
        if (action.indexOf("/cart/add") !== -1) mark();
      },
      true
    );
    // PUB_SUB_EVENTS is a top level const in the theme, not a window property
    if (window.FoxThemeEvents && typeof PUB_SUB_EVENTS !== "undefined") {
      window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, mark);
    }
  }

  isShopping() {
    if (Date.now() - this.lastCartAt < 4000) return true;
    // the cart drawer is re-rendered on refresh, so look it up each time rather than caching it
    return !!document.querySelector("drawer-component[open]");
  }

  whenNotShopping(fn) {
    const quietFor = 5000;
    let quietSince = this.isShopping() ? 0 : Date.now();
    if (quietSince) return fn();
    const poll = setInterval(() => {
      if (this.hasAttribute("open")) return clearInterval(poll);
      if (this.isShopping()) {
        quietSince = 0;
        return;
      }
      if (!quietSince) quietSince = Date.now();
      if (Date.now() - quietSince >= quietFor) {
        clearInterval(poll);
        fn();
      }
    }, 500);
  }

  // The cookie banner comes first. On a first visit from a region that gets Shopify's consent
  // card (UK and Europe by IP), the popup waits for the visitor's decision and opens half a
  // second after it; where no banner is due it opens at once. Daniil, 2026-10-10.
  whenConsentSettled(fn) {
    const api = () => (window.Shopify && window.Shopify.customerPrivacy) || null;
    const bannerUp = () => {
      const b = document.getElementById("shopify-pc__banner");
      return !!b && getComputedStyle(b).display !== "none" && b.getBoundingClientRect().height > 0;
    };
    const bannerExpected = () => {
      const a = api();
      return !!(a && typeof a.shouldShowBanner === "function" && a.shouldShowBanner());
    };
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      fn();
    };
    const started = Date.now();
    const check = () => {
      if (done) return;
      if (bannerUp()) {
        // the card is on screen: open half a second after it goes, whichever signal comes first
        document.addEventListener("visitorConsentCollected", () => setTimeout(go, 500), { once: true });
        const poll = setInterval(() => {
          if (done) return clearInterval(poll);
          if (!bannerUp()) {
            clearInterval(poll);
            setTimeout(go, 500);
          }
        }, 300);
        return;
      }
      // the privacy script may not have loaded, or the card may be about to be injected: give it a moment
      if ((!api() || bannerExpected()) && Date.now() - started < 5000) return setTimeout(check, 200);
      go();
    };
    check();
  }

  pickPrize() {
    const total = this.prizes.reduce((sum, p) => sum + (p.weight || 1), 0);
    let roll = Math.random() * total;
    for (let i = 0; i < this.prizes.length; i++) {
      roll -= this.prizes[i].weight || 1;
      if (roll <= 0) return i;
    }
    return 0;
  }

  setPrize(index) {
    this.prizeIndex = Math.min(Math.max(index || 0, 0), this.prizes.length - 1);
    const prize = this.prizes[this.prizeIndex] || {};
    this.querySelectorAll("[data-prize-label]").forEach((el) => {
      el.textContent = prize.label || "";
    });
    if (this.codeInput) this.codeInput.value = prize.code || "";
    if (this.applyLink && prize.code) {
      // fallback href only (the click is intercepted): apply the code, land back here
      const here = window.location.pathname + window.location.search;
      this.applyLink.href = "/discount/" + encodeURIComponent(prize.code) + "?redirect=" + encodeURIComponent(here);
    }
    const state = this.readState();
    if (state.state !== "claimed") {
      this.writeState({ state: state.state || "shown", prize: this.prizeIndex });
    }
  }

  setStage(stage) {
    this.dataset.state = stage;
    if (stage === "scratch") {
      requestAnimationFrame(() => this.initCanvas());
    }
  }

  /* ---------- open / close ---------- */

  open(skipCookie) {
    this.hideTeaser();
    this.setAttribute("open", "true");
    this.removeAttribute("hidden");
    document.body.classList.add("prevent-scroll");
    document.body.style.paddingRight = getScrollbarWidth() + "px";
    trapFocus(this, this.dialog);
    if (this.dataset.state === "scratch") {
      requestAnimationFrame(() => this.initCanvas());
    }
    if (!skipCookie && !this.designMode) {
      this.setRepeatCookie();
    }
  }

  close(e) {
    if (e) e.preventDefault();
    removeTrapFocus();
    this.setAttribute("closing", "true");
    this.setAttribute("hidden", "");
    setTimeout(() => {
      this.removeAttribute("open");
      this.removeAttribute("closing");
      document.body.classList.remove("prevent-scroll");
      document.body.style.paddingRight = "";
      // closed without claiming: leave the mystery-discount tab as the way back
      this.showTeaser();
    }, 300);
  }

  /* ---------- teaser tab ---------- */

  showTeaser() {
    if (!this.teaser || this.designMode) return;
    if (this.readState().state === "claimed") return;
    try {
      if (sessionStorage.getItem(this.teaserKey)) return;
    } catch (e) {
      /* storage unavailable, teaser just shows again */
    }
    this.teaser.removeAttribute("hidden");
  }

  hideTeaser() {
    if (this.teaser) this.teaser.setAttribute("hidden", "");
  }

  handleTeaser() {
    if (!this.teaser) return;
    const openButton = this.teaser.querySelector("[data-teaser-open]");
    if (openButton) {
      openButton.addEventListener("click", () => {
        const state = this.readState();
        if (state.state === "revealed" || state.state === "pending_claim" || this.variant === "welcome") {
          // already scratched (or nothing to scratch): straight to the email step
          this.setPrize(this.prizeFromState(state));
          this.setStage("claim");
        } else {
          this.setPrize(typeof state.prize === "number" ? state.prize : this.pickPrize());
          this.setStage("scratch");
        }
        this.open(true);
      });
    }
    const closeButton = this.teaser.querySelector("[data-teaser-close]");
    if (closeButton) {
      closeButton.addEventListener("click", () => {
        try {
          sessionStorage.setItem(this.teaserKey, "1");
        } catch (e) {
          /* storage unavailable */
        }
        this.hideTeaser();
      });
    }
  }

  handleClose() {
    this.addEventListener("click", (e) => {
      if (e.target === this || e.target.closest("[data-scratch-close]")) this.close(e);
    });
  }

  /* ---------- scratch canvas ---------- */

  initCanvas() {
    if (!this.canvas || this.canvasReady) return;
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width) return;

    if (this.reducedMotion) {
      if (this.tapButton) this.tapButton.removeAttribute("hidden");
      this.canvas.classList.add("kfk-scratch__canvas--static");
    }

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    const ctx = this.canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    this.ctx = ctx;
    this.cssWidth = rect.width;
    this.cssHeight = rect.height;
    this.canvasReady = true;

    this.paintFoil();
    if (!this.reducedMotion) this.bindScratch();
  }

  paintFoil() {
    const ctx = this.ctx;
    const w = this.cssWidth;
    const h = this.cssHeight;

    const gradient = ctx.createLinearGradient(0, 0, w, h);
    gradient.addColorStop(0, "#E9C4A8");
    gradient.addColorStop(0.35, "#DDA987");
    gradient.addColorStop(0.55, "#F1D6C0");
    gradient.addColorStop(0.75, "#D9A47F");
    gradient.addColorStop(1, "#E8C0A2");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, w, h);

    // soft sheen bands
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = "#FFFFFF";
    for (let i = -1; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(i * (w / 3), 0);
      ctx.lineTo(i * (w / 3) + w / 7, 0);
      ctx.lineTo(i * (w / 3) - w / 6 + w / 7, h);
      ctx.lineTo(i * (w / 3) - w / 6, h);
      ctx.closePath();
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // fine speckle so it reads as foil
    for (let i = 0; i < 420; i++) {
      const x = ((i * 97) % 1000) / 1000 * w;
      const y = ((i * 389) % 1000) / 1000 * h;
      ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.16)" : "rgba(139,61,78,0.08)";
      ctx.fillRect(x, y, 1.2, 1.2);
    }

    ctx.fillStyle = "rgba(82,48,58,0.85)";
    ctx.font = "500 19px " + (getComputedStyle(document.body).fontFamily || "sans-serif");
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    const label = this.canvas.dataset.label || "SCRATCH HERE";
    ctx.save();
    const spaced = label.split("").join("  ");
    ctx.fillText(spaced, w / 2, h / 2);
    ctx.restore();
  }

  bindScratch() {
    let scratching = false;
    let last = null;
    let strokes = 0;

    const point = (e) => {
      const rect = this.canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const scratchTo = (p) => {
      const ctx = this.ctx;
      ctx.globalCompositeOperation = "destination-out";
      // must be fully opaque: with destination-out, stroke alpha = how much foil each pass removes
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.lineWidth = 42;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(last ? last.x : p.x, last ? last.y : p.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last = p;
      strokes++;
      if (strokes % 6 === 0) this.checkProgress();
    };

    this.canvas.addEventListener("pointerdown", (e) => {
      scratching = true;
      last = null;
      this.canvas.setPointerCapture(e.pointerId);
      scratchTo(point(e));
    });
    this.canvas.addEventListener("pointermove", (e) => {
      if (scratching) scratchTo(point(e));
    });
    const stop = () => {
      if (!scratching) return;
      scratching = false;
      last = null;
      this.checkProgress();
    };
    this.canvas.addEventListener("pointerup", stop);
    this.canvas.addEventListener("pointercancel", stop);
  }

  checkProgress() {
    if (this.revealed) return;
    const { width, height } = this.canvas;
    const data = this.ctx.getImageData(0, 0, width, height).data;
    let clear = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 4 * 7) {
      total++;
      if (data[i] < 128) clear++;
    }
    if (clear / total >= 0.5) this.reveal();
  }

  reveal() {
    if (this.revealed) return;
    this.revealed = true;
    this.classList.add("is-revealed");
    const state = this.readState();
    this.writeState({ state: "revealed", prize: state.prize });
    setTimeout(() => {
      this.setStage("claim");
      const email = this.querySelector('input[type="email"]');
      if (email && !this.designMode) email.focus({ preventScroll: true });
    }, 900);
  }

  handleTapReveal() {
    if (this.tapButton) {
      this.tapButton.addEventListener("click", () => this.reveal());
    }
  }

  /* ---------- claim / success ---------- */

  handleSubmit() {
    if (!this.form) return;
    this.claimButton = this.form.querySelector("[data-claim-submit]");
    this.sink = this.querySelector('iframe[name="kfk-scratch-sink"]');
    this.claimStarted = false;

    // The submission (including Shopify's captcha re-submit, which ignores
    // preventDefault) lands in the hidden iframe, so this page never reloads.
    this.form.setAttribute("target", "kfk-scratch-sink");

    if (this.sink) {
      this.sink.addEventListener("load", () => {
        if (this.claimStarted) this.finishClaim();
      });
    }

    this.form.addEventListener("submit", (e) => {
      const prize = this.prizes[this.prizeIndex] || {};
      if (this.tagsInput && prize.code) {
        this.tagsInput.value =
          this.variant === "welcome"
            ? "newsletter, welcome-popup, welcome-" + prize.code
            : "newsletter, scratch-card, scratch-" + prize.code;
      }
      if (this.designMode) {
        e.preventDefault();
        this.setStage(this.nextAfterClaim());
        return;
      }
      this.writeState({ state: "pending_claim", prize: this.prizeIndex });
      // Claimed or not, the popup should not chase them around the site
      this.setRepeatCookie();
      this.pushToKlaviyo();
      this.claimStarted = true;
      if (this.claimButton) {
        this.claimButton.classList.add("is-loading");
      }
      // safety net: if the iframe never reports back, show the code anyway
      this.claimTimer = setTimeout(() => this.finishClaim(), 8000);
    });
  }

  clientEvent(name, props, email) {
    const k = this.config.klaviyo || {};
    if (!k.companyId || !email) return;
    fetch("https://a.klaviyo.com/client/events/?company_id=" + encodeURIComponent(k.companyId), {
      method: "POST",
      headers: { "Content-Type": "application/json", revision: "2024-10-15" },
      body: JSON.stringify({
        data: {
          type: "event",
          attributes: {
            metric: { data: { type: "metric", attributes: { name: name } } },
            profile: { data: { type: "profile", attributes: { email: email } } },
            properties: props,
          },
        },
      }),
    }).catch(() => {});
  }

  pushToKlaviyo() {
    const k = this.config.klaviyo || {};
    const emailInput = this.form.querySelector('input[type="email"]');
    const email = emailInput ? emailInput.value.trim() : "";
    if (!email) return;
    const prize = this.prizes[this.prizeIndex] || {};
    const headers = { "Content-Type": "application/json", revision: "2024-10-15" };

    // Klaviyo's onsite object (from the app embed) is the reliable browser
    // transport: raw client-API fetches fail its CORS preflight. This fires
    // the metric that triggers the instant code email.
    if (window.klaviyo) {
      window.klaviyo.identify({
        email: email,
        $email: email,
        "Scratch Prize": prize.label || "",
        "Scratch Code": prize.code || "",
        "Popup Variant": this.variant || "scratch",
      });
      window.klaviyo.track("Claimed Scratch Card Prize", {
        prize: prize.label || "",
        code: prize.code || "",
        variant: this.variant || "scratch",
      });
    }
    // the onsite object silently drops everything until the visitor accepts the
    // cookie banner, so the flow-trigger event also goes over the client API
    // (proven to pass CORS from real browsers)
    this.clientEvent("Claimed Scratch Card Prize", { prize: prize.label || "", code: prize.code || "", variant: this.variant || "scratch" }, email);

    // best-effort direct list subscribe for instant consent; the Shopify
    // integration sync remains the guaranteed consent path if this is blocked
    if (k.companyId && k.listId) {
      fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + encodeURIComponent(k.companyId), {
        method: "POST",
        headers: headers,
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "Scratch Card Popup",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: email,
                    properties: { "Scratch Prize": prize.label || "", "Scratch Code": prize.code || "" },
                  },
                },
              },
            },
            relationships: { list: { data: { type: "list", id: k.listId } } },
          },
        }),
      }).catch(() => {});
    }
  }

  finishClaim() {
    clearTimeout(this.claimTimer);
    if (this.dataset.state === "success" || this.dataset.state === "bday") return;
    this.writeState({ state: "claimed", prize: this.prizeIndex });
    this.applyCode();
    this.setStage(this.nextAfterClaim());
    if (this.claimButton) {
      this.claimButton.classList.remove("is-loading");
    }
  }

  // The code goes on the bag the moment it is claimed, so nobody has to copy it or find a box
  // to type it in. Daniil, 2026-10-17, after a shopper copied KAIA10 and had nowhere to paste it.
  // fetching /discount/CODE sets it on the cart without navigating; the drawer then re-renders
  // with its "Discount KAIA10" line.
  applyCode() {
    const prize = this.prizes[this.prizeIndex] || {};
    if (!prize.code || this.designMode || this.codeApplied) return;
    this.codeApplied = true;
    fetch("/discount/" + encodeURIComponent(prize.code), { credentials: "same-origin" })
      .then(() => document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } })))
      .catch(() => {
        this.codeApplied = false;
      });
  }

  nextAfterClaim() {
    return this.querySelector("[data-bday-step]") && !this.bdayDone ? "bday" : "success";
  }

  handleBirthday() {
    if (!this.querySelector("[data-bday-step]")) return;

    const proceed = () => {
      this.bdayDone = true;
      this.setStage("success");
    };

    const save = this.querySelector("[data-bday-save]");
    if (save) {
      save.addEventListener("click", () => {
        const month = parseInt((this.querySelector("[data-bday-month]") || {}).value, 10);
        const day = parseInt((this.querySelector("[data-bday-day]") || {}).value, 10);
        if (month && day) {
          // clamp to the month's real length (year 2000 is the placeholder for unknown birth years)
          const safeDay = Math.min(day, new Date(2000, month, 0).getDate());
          const iso = "2000-" + ("0" + month).slice(-2) + "-" + ("0" + safeDay).slice(-2);
          const emailInput = this.form && this.form.querySelector('input[type="email"]');
          const email = emailInput ? emailInput.value.trim() : "";
          if (window.klaviyo) {
            const props = {
              "Child Birthday": iso,
              "Child Birthday Month": month,
              "Child Birthday Day": safeDay,
            };
            if (email) {
              props.email = email;
              props.$email = email;
            }
            window.klaviyo.identify(props);
            window.klaviyo.track("Joined Birthday Club", { month: month, day: safeDay });
          }
          this.clientEvent("Joined Birthday Club", {
            month: month,
            day: safeDay,
            "Child Birthday": iso,
          }, email);
        }
        // with nothing selected, Continue is a polite skip
        proceed();
      });
    }
    const skip = this.querySelector("[data-bday-skip]");
    if (skip) {
      skip.addEventListener("click", proceed);
    }
  }

  handleApply() {
    if (!this.applyLink) return;
    this.applyLink.addEventListener("click", (e) => {
      // apply the discount in the background and stay on this page:
      // fetching /discount/CODE sets the cart discount cookie without navigating
      e.preventDefault();
      const prize = this.prizes[this.prizeIndex] || {};
      if (prize.code && !this.designMode) {
        fetch("/discount/" + encodeURIComponent(prize.code), { credentials: "same-origin" }).catch(() => {});
      }
      this.close();
    });
  }

  handleCopy() {
    if (!this.copyButton) return;
    this.copyButton.addEventListener("click", (e) => {
      e.preventDefault();
      const code = this.codeInput ? this.codeInput.value : "";
      if (!code) return;
      navigator.clipboard.writeText(code);
      this.copyButton.classList.add("copied");
      const label = this.copyButton.querySelector("span");
      if (label && this.config.copiedLabel) label.textContent = this.config.copiedLabel;
    });
  }

  /* ---------- theme editor ---------- */

  setupDesignMode() {
    document.addEventListener("shopify:section:select", (e) => {
      if (e.detail.sectionId === this.dataset.sectionId) this.openDemo();
    });
    document.addEventListener("shopify:section:deselect", (e) => {
      if (e.detail.sectionId === this.dataset.sectionId) this.close();
    });
  }

  openDemo() {
    // the Customizer preview shows the first step of whichever type is selected
    this.applyVariant(this.config.mode === "welcome" ? "welcome" : "scratch");
    this.setPrize(0);
    this.setStage(this.firstStage());
    this.open(true);
  }
}

customElements.define("scratch-card-popup", ScratchCardPopup);
